import { useMemo } from 'react';

import { LocalDropdownNotificationType } from '#/types/LocalNotification/LocalDropdownNotificationType';
import type { LocalNotificationData } from '#/types/LocalNotification/LocalNotificationData';
import { LocalNotificationStatus } from '#/types/LocalNotification/LocalNotificationStatus';
import type { LocalNotificationType } from '#/types/LocalNotification/LocalNotificationType';
import { LocalProductNotificationType } from '#/types/LocalNotification/LocalProductNotificationType';
import type { MappedLocalNotification } from '#/types/LocalNotification/MappedLocalNotification';

import { createLocalStorageStore } from './utils/createLocalStorageStore';

const IDEALIST_NOTIFICATIONS_KEY = 'idealistNotifications';

type LocalNotificationsStoreData = Partial<
  Record<LocalNotificationType, LocalNotificationData>
>;

const { getValue, useValue, setValue } =
  createLocalStorageStore<LocalNotificationsStoreData>({
    key: IDEALIST_NOTIFICATIONS_KEY,
    getInitializationValue: (currentNotifications) => {
      const newNotifications: Record<string, LocalNotificationData> = {
        ...currentNotifications,
      };

      // Automatically create notifications
      [
        ...Object.values(LocalDropdownNotificationType),
        ...Object.values(LocalProductNotificationType),
      ].forEach((type) => {
        if (newNotifications[type]) return;

        newNotifications[type] = {
          status: LocalNotificationStatus.NEW,
          viewed: false,
          read: false,
        } satisfies LocalNotificationData;
      });

      return newNotifications;
    },
  });

// Actions

export function getLocalNotification(type: LocalNotificationType) {
  const currentNotifications = getValue() ?? {};

  return currentNotifications?.[type];
}

export function updateLocalNotification(
  type: LocalNotificationType,
  partialValue: Partial<MappedLocalNotification>,
) {
  const currentNotifications = getValue() ?? {};
  const currentNotification = currentNotifications?.[type] ?? {};

  return setValue({
    ...currentNotifications,
    [type]: { ...currentNotification, ...partialValue },
  });
}

export function dismissLocalNotification(type: LocalNotificationType) {
  updateLocalNotification(type, { status: LocalNotificationStatus.DISMISSED });
}

export function markLocalNotificationsAsViewed(types: LocalNotificationType[]) {
  const currentValue = getValue();
  if (!currentValue) return;

  const newValue: LocalNotificationsStoreData = { ...currentValue };

  (Object.keys(newValue) as LocalNotificationType[]).forEach((type) => {
    const valueForType = newValue[type];
    if (!valueForType || !types.includes(type)) return;

    newValue[type] = { ...valueForType, viewed: true };
  });

  setValue(newValue);
}

// Hooks

export function useLocalNotification(
  type: LocalNotificationType,
): LocalNotificationData | undefined {
  const localNotifications = useValue();
  return localNotifications?.[type];
}

/**
 * Transforms data stored as:
 * {
 *   "NOTIFICATION_TYPE": {
 *     status: "NEW"
 *   }
 * }
 *
 * to this format:
 * [
 *   {
 *     type: "NOTIFICATION_TYPE",
 *     status: "NEW"
 *   }
 * ]
 */
export function useMappedLocalNotifications() {
  const localNotifications = useValue();

  return useMemo(
    () =>
      (Object.keys(localNotifications ?? {}) as LocalNotificationType[]).map(
        (type) => {
          const localNotification = localNotifications?.[type];
          return {
            type,
            status: localNotification!.status,
            viewed: localNotification?.viewed,
            read: localNotification?.read,
          } satisfies MappedLocalNotification;
        },
      ),
    [localNotifications],
  );
}
